import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const Accessibility = () => {
  const tableOfContents = [
    { id: 'commitment', title: 'Our Commitment' },
    { id: 'standards', title: 'Accessibility Standards' },
    { id: 'features', title: 'Accessibility Features' },
    { id: 'assistance', title: 'Assistance and Support' },
    { id: 'feedback', title: 'Feedback and Improvement' },
    { id: 'limitations', title: 'Third-Party Limitations' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <section className="py-20">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Table of Contents - Sticky on desktop */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24">
                <h2 className="text-lg font-semibold text-foreground mb-4">Table of Contents</h2>
                <nav className="space-y-2">
                  {tableOfContents.map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {item.title}
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <h1 className="text-4xl font-bold text-foreground mb-4">Accessibility Statement</h1>
              <p className="text-muted-foreground mb-8">Last updated: January 2024</p>
              
              <div className="prose prose-lg max-w-none space-y-8">
                <section id="commitment">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Our Commitment</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>
                      Safurra is committed to ensuring digital accessibility for people with disabilities. We strive to provide 
                      an inclusive experience that allows all users to access our insurance comparison and quote services easily 
                      and effectively.
                    </p>
                    <p>
                      We are continually improving the accessibility of our website and services to ensure we provide equal 
                      access to information and functionality for all our users.
                    </p>
                  </div>
                </section>

                <section id="standards">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Accessibility Standards</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>
                      We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards. 
                      These guidelines help make web content accessible to people with a wide range of disabilities, including:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Visual impairments and blindness</li>
                      <li>Hearing impairments and deafness</li>
                      <li>Motor impairments and physical disabilities</li>
                      <li>Cognitive and neurological disabilities</li>
                    </ul>
                    <div className="bg-muted p-6 rounded-lg">
                      <p>
                        <strong className="text-foreground">WCAG 2.1 AA Compliance:</strong> Our goal is to meet or exceed 
                        Level AA conformance, which includes requirements for perceivable, operable, understandable, 
                        and robust web content.
                      </p>
                    </div>
                  </div>
                </section>

                <section id="features">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Accessibility Features</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>Our website includes the following accessibility features:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong className="text-foreground">Keyboard Navigation:</strong> All interactive elements can be accessed using keyboard-only navigation</li>
                      <li><strong className="text-foreground">Screen Reader Compatibility:</strong> Content is structured with proper headings, labels, and semantic markup</li>
                      <li><strong className="text-foreground">Alternative Text:</strong> Images include descriptive alt text for screen readers</li>
                      <li><strong className="text-foreground">Color Contrast:</strong> Text and background colors meet or exceed WCAG contrast requirements</li>
                      <li><strong className="text-foreground">Responsive Design:</strong> Content adapts to different screen sizes and zoom levels up to 200%</li>
                      <li><strong className="text-foreground">Focus Indicators:</strong> Clear visual focus indicators for keyboard navigation</li>
                      <li><strong className="text-foreground">Skip Links:</strong> Skip navigation links to jump to main content</li>
                      <li><strong className="text-foreground">Form Labels:</strong> All form inputs have associated labels and instructions</li>
                    </ul>
                  </div>
                </section>

                <section id="assistance">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Assistance and Support</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>
                      If you encounter any accessibility barriers while using our website or need assistance with any of our services, 
                      we're here to help. Please contact our support team:
                    </p>
                    <div className="bg-muted p-6 rounded-lg space-y-3">
                      <p><strong className="text-foreground">Email:</strong> <a href="mailto:support@safurra.com" className="text-primary hover:underline">support@safurra.com</a></p>
                      <p><strong className="text-foreground">Subject Line:</strong> "Accessibility Support Request"</p>
                      <p><strong className="text-foreground">Response Time:</strong> We aim to respond to accessibility requests within 1 business day</p>
                    </div>
                    <p>
                      When contacting us about accessibility, please provide:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>A description of the specific issue or barrier you encountered</li>
                      <li>The web page or feature where you experienced the problem</li>
                      <li>The assistive technology you were using (if applicable)</li>
                      <li>Your preferred method of receiving a response</li>
                    </ul>
                  </div>
                </section>

                <section id="feedback">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Feedback and Improvement</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>
                      We welcome feedback about the accessibility of our website and services. Your input helps us identify 
                      areas for improvement and ensures we continue to provide an accessible experience for all users.
                    </p>
                    <p>
                      <strong className="text-foreground">Ongoing Improvements:</strong> We regularly review and test our website 
                      for accessibility issues and implement improvements based on user feedback and evolving best practices.
                    </p>
                    <p>
                      <strong className="text-foreground">Accessibility Audits:</strong> We conduct periodic accessibility audits 
                      with assistive technology users and accessibility experts to identify and address potential barriers.
                    </p>
                  </div>
                </section>

                <section id="limitations">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Third-Party Limitations</h2>
                  <div className="text-muted-foreground space-y-4">
                    <p>
                      While we strive to ensure accessibility across our entire platform, some third-party content and services 
                      integrated into our website may not be fully under our control. This includes:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>External carrier websites and application forms</li>
                      <li>Third-party payment processing systems</li>
                      <li>Embedded content from partner organizations</li>
                      <li>Social media widgets and plugins</li>
                    </ul>
                    <p>
                      We work with our partners to encourage accessibility improvements and will assist you in accessing 
                      alternative methods to complete any processes that may not be fully accessible.
                    </p>
                    <div className="bg-muted p-6 rounded-lg">
                      <p>
                        <strong className="text-foreground">Alternative Access:</strong> If you encounter accessibility issues 
                        with third-party content, please contact us at{' '}
                        <a href="mailto:support@safurra.com" className="text-primary hover:underline">support@safurra.com</a>{' '}
                        and we will work to provide alternative access methods or assistance.
                      </p>
                    </div>
                  </div>
                </section>

                <div className="mt-8 p-6 bg-primary text-primary-foreground rounded-lg">
                  <h3 className="text-lg font-semibold mb-2">Accessibility is an Ongoing Journey</h3>
                  <p>
                    We view accessibility as an ongoing commitment, not a one-time achievement. We continuously work to 
                    improve the accessibility of our platform and welcome your feedback as we strive to serve all users effectively.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Accessibility;